import { useCallback, useEffect, useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { DataTable, DataTableAction, DataTableColumn } from '@/components/admin/DataTable'
import { apiClient } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'

// Admin Panel Redesign — brand new section. Surfaces the per-user
// subscription/billing table (tier, Dodo customer id, usage) plus the newly
// durable log of incoming Dodo Payments webhook events (previously nothing
// was persisted for webhooks — only a 24h Redis dedupe key). Also relocates
// the pre-existing PUT /admin/subscriptions tier-override action here, out
// of the old Users tab, as a per-row "Override tier" dialog.

interface SubscriptionRow {
  user_id: string
  email: string
  subscription_tier: string
  dodo_customer_id: string | null
  requests_used: number
  request_limit: number
  is_active: boolean
}

interface WebhookEventRow {
  id: string
  event_type: string
  dodo_customer_id: string | null
  dodo_subscription_id: string | null
  user_id: string | null
  status: 'processed' | 'failed' | 'duplicate'
  error: string | null
  received_at: string
}

const PAGE_SIZE = 50

const TIER_OPTIONS = [
  { value: 'freemium', label: 'Freemium' },
  { value: 'starter', label: 'Starter' },
  { value: 'professional', label: 'Professional' },
  { value: 'enterprise', label: 'Enterprise' },
]

// Dodo sends a free-form `event_type` string — the backend doesn't validate
// it against a closed enum, it just persists whatever arrives. This list
// covers the event types the backend/docs currently reference; "All event
// types" always remains available regardless of whether a type below shows
// up in the actual feed.
const EVENT_TYPE_OPTIONS = [
  'payment.succeeded',
  'payment.failed',
  'payment.processing',
  'payment.cancelled',
  'subscription.active',
  'subscription.renewed',
  'subscription.canceled',
  'subscription.on_hold',
  'subscription.plan_changed',
  'subscription.expired',
  'refund.succeeded',
  'refund.failed',
  'dispute.opened',
]

const STATUS_OPTIONS: WebhookEventRow['status'][] = ['processed', 'failed', 'duplicate']

function formatDate(iso: string) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString()
}

function statusBadgeClass(status: string) {
  switch (status) {
    case 'processed':
      return 'border-green-500/30 bg-green-500/10 text-green-500'
    case 'failed':
      return 'border-red-500/30 bg-red-500/10 text-red-500'
    case 'duplicate':
      return 'border-muted-foreground/30 bg-muted text-muted-foreground'
    default:
      return ''
  }
}

function PaginationControls({
  skip,
  limit,
  total,
  onPageChange,
  disabled,
}: {
  skip: number
  limit: number
  total: number
  onPageChange: (skip: number) => void
  disabled?: boolean
}) {
  const start = total === 0 ? 0 : skip + 1
  const end = Math.min(skip + limit, total)
  return (
    <div className="flex items-center justify-between pt-3">
      <span className="text-xs text-muted-foreground">
        {total === 0 ? '0 results' : `${start}–${end} of ${total}`}
      </span>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={disabled || skip === 0}
          onClick={() => onPageChange(Math.max(0, skip - limit))}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled || skip + limit >= total}
          onClick={() => onPageChange(skip + limit)}
        >
          Next
        </Button>
      </div>
    </div>
  )
}

export default function SubscriptionsSection() {
  const { toast } = useToast()

  // Subscriptions table state
  const [subscriptions, setSubscriptions] = useState<SubscriptionRow[]>([])
  const [subsTotal, setSubsTotal] = useState(0)
  const [subsSkip, setSubsSkip] = useState(0)
  const [subsLoading, setSubsLoading] = useState(false)
  const [subsError, setSubsError] = useState<string | null>(null)
  const [subsSearch, setSubsSearch] = useState('')

  // Webhook events table state
  const [events, setEvents] = useState<WebhookEventRow[]>([])
  const [eventsTotal, setEventsTotal] = useState(0)
  const [eventsSkip, setEventsSkip] = useState(0)
  const [eventsLoading, setEventsLoading] = useState(false)
  const [eventsError, setEventsError] = useState<string | null>(null)
  const [eventTypeFilter, setEventTypeFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')

  // Override-tier dialog state
  const [overrideOpen, setOverrideOpen] = useState(false)
  const [overrideTarget, setOverrideTarget] = useState<SubscriptionRow | null>(null)
  const [overrideTier, setOverrideTier] = useState('freemium')
  const [overrideLimit, setOverrideLimit] = useState('')
  const [overrideSubmitting, setOverrideSubmitting] = useState(false)

  const loadSubscriptions = useCallback(async (skip: number) => {
    try {
      setSubsLoading(true)
      setSubsError(null)
      const res = await apiClient.adminListSubscriptions(skip, PAGE_SIZE)
      setSubscriptions(res.subscriptions)
      setSubsTotal(res.total)
      setSubsSkip(res.skip)
    } catch (e: any) {
      console.error('Failed to load subscriptions', e)
      setSubsError(e.message || 'Failed to load subscriptions')
    } finally {
      setSubsLoading(false)
    }
  }, [])

  const loadEvents = useCallback(async (skip: number, eventType: string, status: string) => {
    try {
      setEventsLoading(true)
      setEventsError(null)
      const res = await apiClient.adminListWebhookEvents({
        skip,
        limit: PAGE_SIZE,
        event_type: eventType !== 'all' ? eventType : undefined,
        status: status !== 'all' ? status : undefined,
      })
      setEvents(res.events as WebhookEventRow[])
      setEventsTotal(res.total)
      setEventsSkip(res.skip)
    } catch (e: any) {
      console.error('Failed to load webhook events', e)
      setEventsError(e.message || 'Failed to load webhook events')
    } finally {
      setEventsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadSubscriptions(0)
  }, [loadSubscriptions])

  useEffect(() => {
    loadEvents(0, eventTypeFilter, statusFilter)
  }, [eventTypeFilter, statusFilter, loadEvents])

  const filteredSubscriptions = useMemo(() => {
    const q = subsSearch.trim().toLowerCase()
    if (!q) return subscriptions
    return subscriptions.filter(
      (s) =>
        s.email.toLowerCase().includes(q) ||
        s.subscription_tier.toLowerCase().includes(q) ||
        (s.dodo_customer_id || '').toLowerCase().includes(q)
    )
  }, [subscriptions, subsSearch])

  const openOverride = (row: SubscriptionRow) => {
    setOverrideTarget(row)
    setOverrideTier(row.subscription_tier)
    setOverrideLimit('')
    setOverrideOpen(true)
  }

  const handleOverrideSubmit = async () => {
    if (!overrideTarget) return
    const payload: { user_id: string; tier: string; custom_limit?: number } = {
      user_id: overrideTarget.user_id,
      tier: overrideTier,
    }
    if (overrideLimit.trim() !== '') {
      const parsed = Number(overrideLimit)
      if (Number.isNaN(parsed)) {
        toast({ title: 'Custom limit must be a number', variant: 'destructive' })
        return
      }
      payload.custom_limit = parsed
    }
    try {
      setOverrideSubmitting(true)
      await apiClient.updateUserSubscription(payload)
      toast({ title: `Updated ${overrideTarget.email} to ${overrideTier}` })
      setOverrideOpen(false)
      setOverrideTarget(null)
      await loadSubscriptions(subsSkip)
    } catch (e: any) {
      console.error('Failed to override subscription tier', e)
      toast({
        title: 'Failed to update subscription',
        description: e.message || 'Unknown error',
        variant: 'destructive',
      })
    } finally {
      setOverrideSubmitting(false)
    }
  }

  const subscriptionColumns: DataTableColumn<SubscriptionRow>[] = [
    {
      key: 'email',
      header: 'Email',
      render: (r) => <span className="font-medium">{r.email}</span>,
    },
    {
      key: 'tier',
      header: 'Tier',
      render: (r) => (
        <Badge variant="outline" className="capitalize">
          {r.subscription_tier}
        </Badge>
      ),
    },
    {
      key: 'dodo_customer_id',
      header: 'Dodo Customer ID',
      render: (r) => (
        <span className="font-mono text-xs">{r.dodo_customer_id || '—'}</span>
      ),
    },
    {
      key: 'usage',
      header: 'Requests Used / Limit',
      render: (r) => (
        <span className="text-sm">
          {r.requests_used} / {r.request_limit === -1 ? 'Unlimited' : r.request_limit}
        </span>
      ),
    },
    {
      key: 'is_active',
      header: 'Status',
      render: (r) => (
        <Badge variant={r.is_active ? 'default' : 'secondary'}>
          {r.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ]

  const subscriptionActions: DataTableAction<SubscriptionRow>[] = [
    {
      label: 'Override tier',
      onClick: openOverride,
    },
  ]

  const eventColumns: DataTableColumn<WebhookEventRow>[] = [
    {
      key: 'received_at',
      header: 'Received At',
      render: (r) => <span className="whitespace-nowrap text-xs">{formatDate(r.received_at)}</span>,
    },
    {
      key: 'event_type',
      header: 'Event Type',
      render: (r) => <span className="font-mono text-xs">{r.event_type}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <Badge variant="outline" className={cn('capitalize', statusBadgeClass(r.status))}>
          {r.status}
        </Badge>
      ),
    },
    {
      key: 'dodo_customer_id',
      header: 'Dodo Customer ID',
      render: (r) => (
        <span className="font-mono text-xs">{r.dodo_customer_id || '—'}</span>
      ),
    },
    {
      key: 'dodo_subscription_id',
      header: 'Dodo Subscription ID',
      render: (r) => (
        <span className="font-mono text-xs">{r.dodo_subscription_id || '—'}</span>
      ),
    },
    {
      key: 'user_id',
      header: 'User ID',
      render: (r) => <span className="font-mono text-xs">{r.user_id || '—'}</span>,
    },
    {
      key: 'error',
      header: 'Error',
      render: (r) =>
        r.error ? (
          <span
            title={r.error}
            className="block max-w-[220px] truncate text-xs text-destructive"
          >
            {r.error}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
  ]

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>Subscriptions</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Per-user billing tier, Dodo customer linkage, and usage against plan limits.
            </p>
          </div>
          <div className="w-full sm:w-72">
            <Input
              placeholder="Filter by email, tier, or customer ID..."
              value={subsSearch}
              onChange={(e) => setSubsSearch(e.target.value)}
            />
          </div>
        </CardHeader>
        <CardContent>
          {subsError && <p className="mb-2 text-sm text-destructive">{subsError}</p>}
          <DataTable
            columns={subscriptionColumns}
            rows={filteredSubscriptions}
            getRowId={(r) => r.user_id}
            rowActions={subscriptionActions}
            isLoading={subsLoading}
            emptyMessage="No subscriptions found."
          />
          <PaginationControls
            skip={subsSkip}
            limit={PAGE_SIZE}
            total={subsTotal}
            onPageChange={loadSubscriptions}
            disabled={subsLoading}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="space-y-3">
          <div>
            <CardTitle>Recent Webhook Events</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Durable log of incoming Dodo Payments webhook events. Events only exist from when
              this instrumentation shipped, so an empty feed on a fresh deploy is expected.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="w-full sm:w-64">
              <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All event types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All event types</SelectItem>
                  {EVENT_TYPE_OPTIONS.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-48">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s} className="capitalize">
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {eventsError && <p className="mb-2 text-sm text-destructive">{eventsError}</p>}
          <DataTable
            columns={eventColumns}
            rows={events}
            getRowId={(r) => r.id}
            isLoading={eventsLoading}
            emptyMessage="No webhook events yet."
          />
          <PaginationControls
            skip={eventsSkip}
            limit={PAGE_SIZE}
            total={eventsTotal}
            onPageChange={(skip) => loadEvents(skip, eventTypeFilter, statusFilter)}
            disabled={eventsLoading}
          />
        </CardContent>
      </Card>

      <Dialog
        open={overrideOpen}
        onOpenChange={(open) => {
          setOverrideOpen(open)
          if (!open) setOverrideTarget(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Override Subscription Tier</DialogTitle>
            {overrideTarget && (
              <DialogDescription>
                {overrideTarget.email} — currently {overrideTarget.subscription_tier} (
                {overrideTarget.requests_used}/
                {overrideTarget.request_limit === -1 ? 'Unlimited' : overrideTarget.request_limit}
                )
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tier</Label>
              <Select value={overrideTier} onValueChange={setOverrideTier}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIER_OPTIONS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Custom Request Limit (optional)</Label>
              <Input
                type="number"
                placeholder={`Leave blank to use the ${overrideTier} tier default`}
                value={overrideLimit}
                onChange={(e) => setOverrideLimit(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Enter -1 for unlimited requests.</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOverrideOpen(false)}
              disabled={overrideSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleOverrideSubmit} disabled={overrideSubmitting}>
              {overrideSubmitting ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
